// VerifyModal
// -----------
// Modal that shows ownership-verification instructions and triggers the check.
//
// Verification is HTTP-layer (DNS removed) and covers EVERY target: the web-app
// META TAG plus, for each configured API domain, a nimis-verify endpoint. One
// verification token is used for all of them. The backend fails the whole check
// if any one target is missing and tells us which, so the user can fix exactly
// that one. The frontend never performs the check itself — it calls the backend
// verify mutation and displays the result.

import React, { useState, useCallback, useMemo, useRef } from "react";
import { X, Copy, Check } from "lucide-react";
import ReactDOM from "react-dom";
import { Button } from "@/components/ui/button";
import { useModalAccessibility } from "@/utils/useModalAccessibility";
import { getVerificationTargets } from "@/utils/verificationTargets";
import { verifyAppOwnership } from "@/services/dataListOperations.service";
import WebApplicationClass from "@/models/webApplication";

const VERIFY_STATUS_IDLE = "idle";
const VERIFY_STATUS_CHECKING = "checking";
const VERIFY_STATUS_FAILED = "failed";

type VerifyStatus =
  | typeof VERIFY_STATUS_IDLE
  | typeof VERIFY_STATUS_CHECKING
  | typeof VERIFY_STATUS_FAILED;

const DEFAULT_FAILURE_MESSAGE =
  "Verification failed — the token was not found. Double-check the meta tag and every nimis-verify endpoint are in place and served over HTTPS, then try again.";

const COPIED_RESET_MS = 2000;
const COPY_KEY_TOKEN = "__token__";

interface VerifyModalProps {
  webapp: WebApplicationClass;
  show: boolean;
  close: () => void;
  onVerified: () => void;
}

function VerifyModal({ webapp, show, close, onVerified }: VerifyModalProps) {
  const [verifyStatus, setVerifyStatus] = useState<VerifyStatus>(VERIFY_STATUS_IDLE);
  const [failureMessage, setFailureMessage] = useState<string>(DEFAULT_FAILURE_MESSAGE);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const verificationString = webapp.verificationString || "";

  // Web-app meta tag + one nimis-verify endpoint per configured API domain.
  const targets = useMemo(
    () => getVerificationTargets(webapp.url, webapp.apiDomains, verificationString),
    [webapp.url, webapp.apiDomains, verificationString],
  );

  const hasApiTargets = targets.some((target) => !target.isPrimary);
  const tokenReady = !!verificationString && targets.length > 0;

  const handleClose = useCallback(() => {
    setVerifyStatus(VERIFY_STATUS_IDLE);
    setFailureMessage(DEFAULT_FAILURE_MESSAGE);
    setCopiedKey(null);
    close();
  }, [close]);

  const copyValue = useCallback(async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), COPIED_RESET_MS);
    } catch {
      // clipboard API unavailable — silent fail; the value is still selectable.
    }
  }, []);

  const handleVerify = useCallback(async () => {
    setVerifyStatus(VERIFY_STATUS_CHECKING);
    try {
      // Richer result: { verified, failedDomain, message }. A missing/failed
      // result (network/auth error → null) is treated as a generic failure.
      const result = await verifyAppOwnership(webapp.id);
      if (result?.verified) {
        onVerified();
        handleClose();
      } else {
        setFailureMessage(result?.message || DEFAULT_FAILURE_MESSAGE);
        setVerifyStatus(VERIFY_STATUS_FAILED);
      }
    } catch {
      setFailureMessage(DEFAULT_FAILURE_MESSAGE);
      setVerifyStatus(VERIFY_STATUS_FAILED);
    }
  }, [webapp.id, onVerified, handleClose]);

  const dialogContainerRef = useRef<HTMLDivElement>(null);
  useModalAccessibility(show, handleClose, dialogContainerRef);

  if (!show) return null;

  const isChecking = verifyStatus === VERIFY_STATUS_CHECKING;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Modal */}
      <div
        ref={dialogContainerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Verify Ownership"
        className="relative w-full max-w-lg rounded-xl bg-white shadow-xl flex flex-col max-h-[90vh]"
        onClick={(clickEvent) => clickEvent.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-base font-semibold text-gray-900">Verify Ownership</h2>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <p className="text-sm text-gray-700">
              Add the meta tag to your web app
              {hasApiTargets ? " and serve a nimis-verify endpoint for each API domain" : ""}, then click{" "}
              <span className="font-medium text-gray-700">Verify</span>.
            </p>
            <p className="text-xs text-gray-400">
              {hasApiTargets
                ? "Every item below must be in place — verification fails until all of them are served. "
                : ""}
              All of it must be served over HTTPS — an application served over
              plain HTTP is inherently insecure and isn't tested.
            </p>
          </div>

          {!tokenReady ? (
            <p className="text-sm text-red-600">
              Verification token unavailable — please close this dialog and try again.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {/* Token */}
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-600">Verification token</span>
                <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <code className="flex-1 truncate text-xs text-gray-700">{verificationString}</code>
                  <button
                    type="button"
                    onClick={() => copyValue(COPY_KEY_TOKEN, verificationString)}
                    className="shrink-0 rounded-md p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer"
                    title="Copy token"
                  >
                    {copiedKey === COPY_KEY_TOKEN ? (
                      <Check className="h-3.5 w-3.5 text-emerald-500" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Targets */}
              {targets.map((target) => (
                <div key={target.label} className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-600 break-all">
                      {target.label}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-gray-400">
                      {target.isPrimary ? "Web App" : "API"}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    {target.isPrimary
                      ? "Add this tag to the page at your web-app URL:"
                      : "Serve this endpoint returning the token as text/plain:"}
                  </p>
                  <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <code className="flex-1 truncate text-xs text-gray-700">{target.copyValue}</code>
                    <button
                      type="button"
                      onClick={() => copyValue(target.label, target.copyValue)}
                      className="shrink-0 rounded-md p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer"
                      title="Copy"
                    >
                      {copiedKey === target.label ? (
                        <Check className="h-3.5 w-3.5 text-emerald-500" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Error message — names the failing target when the backend reports one */}
          {verifyStatus === VERIFY_STATUS_FAILED && (
            <p className="text-sm text-red-600">{failureMessage}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 shrink-0">
          <Button variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleVerify} disabled={isChecking || !tokenReady}>
            {isChecking ? "Checking…" : "Verify"}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default VerifyModal;
